import React from "react";

import { Card } from "../cards/card";
import {
    BadgeAlignment,
    BadgeStyle,
    CardIconType,
    CardLayout
} from "../cards/card-constants";
import { formatCurrency, formatDateTimeValue, formatDateValue, formatTimeValue } from "../functions/locales";
import { TagChipList } from "../tag-chip/tag-chip";
import {
    CardPosition,
    TableFieldType
} from "./table-constants";

function formatCardValue(column, row, locale) {
    const value = row[column.FieldName];
    if (column.FieldType === TableFieldType.Tags) {
        const tags = Array.isArray(value) ? value : [];
        if (tags.length === 0) return "";
        return <TagChipList Tags={tags} />;
    }
    if (value === undefined || value === null) return "";
    switch (column.FieldType) {
        // Same helpers the grid rows use, so a card and a row never disagree about what day it is.
        case TableFieldType.Date:
            return formatDateValue(value, locale);
        case TableFieldType.Time:
            return formatTimeValue(value, locale);
        case TableFieldType.DateTime:
            return formatDateTimeValue(value, locale);
        case TableFieldType.Currency:
            return formatCurrency(value, (column.CurrencyField ? row[column.CurrencyField] : null) || column.Currency, locale);
        // A Select column's row value is its option's KEY, so a card has to resolve it the way
        // buildBadge below already does and the grid row does through getSelectLabelValue.
        // Without this a Select column outside the Badge position printed the raw key -- a card
        // reading "connectors" where the grid beside it read "Connectors". Falls back to the key,
        // matching the grid, so a value whose option has gone is still legible rather than blank.
        case TableFieldType.Select: {
            const option = column.Options?.find(opt => opt.Key === value);
            return option && option.Value !== undefined && option.Value !== null
                ? option.Value.toString()
                : value.toString();
        }
        default:
            return value.toString();
    }
}

function buildIcon(props) {
    const { CardIconType: iconTypeProp, CardIconInitialsColumn, CardIconAvatarColumn, CardIconImageColumn, CardIconSeedColumn, Columns, Row, CardIconImageHideBackground } = props;

    if (!iconTypeProp || iconTypeProp === CardIconType.None) {
        return { IconType: CardIconType.None };
    }

    // CardIconSeedColumn lets the hashed colour key off something unique per row (an email) while the
    // initials still come from the display name, so the same person keeps one colour everywhere.
    const seedValue = CardIconSeedColumn ? Row[CardIconSeedColumn] : null;

    if (iconTypeProp === CardIconType.Avatar) {
        const avatarSrc = CardIconAvatarColumn ? Row[CardIconAvatarColumn] : null;
        const initialsValue = CardIconInitialsColumn ? Row[CardIconInitialsColumn] : null;
        return {
            IconType: CardIconType.Avatar,
            Icon: avatarSrc,
            InitialsValue: initialsValue,
            SeedValue: seedValue
        };
    }

    if (iconTypeProp === CardIconType.Initials) {
        const initialsValue = CardIconInitialsColumn ? Row[CardIconInitialsColumn] : null;
        return {
            IconType: CardIconType.Initials,
            Icon: initialsValue,
            SeedValue: seedValue
        };
    }

    // Per-row image override: when a row supplies an image URL, render it as a logo image in the
    // same square icon slot (object-fit: contain), regardless of the table-wide icon type. Rows
    // without one fall through to the normal font-icon resolution below, so mixed image/font-icon
    // grids work.
    const imageSrc = CardIconImageColumn ? Row[CardIconImageColumn] : null;
    if (imageSrc) {
        return {
            IconType: CardIconType.Image,
            Icon: imageSrc,
            HideBackground: CardIconImageHideBackground
        };
    }

    const iconColumn = Columns.find(column => column.CardPosition === CardPosition.Icon)
        || Columns.find(column => column.Icon);
    if (!iconColumn) return { IconType: CardIconType.None };

    let icon = iconColumn.Icon;
    let iconColor = iconColumn.IconColor;
    let iconType = iconColumn.IconType || iconTypeProp;

    if (iconColumn.Options) {
        const rowValue = Row[iconColumn.FieldName];
        const option = iconColumn.Options.find(opt => opt.Key === rowValue);
        if (option) {
            if (option.Icon !== undefined) icon = option.Icon;
            if (option.IconColor !== undefined) iconColor = option.IconColor;
            if (option.IconType !== undefined) {
                if (option.IconType === "af") iconType = CardIconType.AppFontIcon;
                else if (option.IconType === "fa") iconType = CardIconType.FontAwesomeIcon;
                else iconType = option.IconType;
            }
        }
    }

    if (iconType === CardIconType.Initials) {
        const titleColumn = Columns.find(column => column.CardPosition === CardPosition.Title);
        const initialsValue = titleColumn ? Row[titleColumn.FieldName] : null;
        return {
            IconType: CardIconType.Initials,
            Icon: initialsValue,
            IconColor: iconColor
        };
    }

    if (!icon) return { IconType: CardIconType.None };

    return {
        IconType: iconType === CardIconType.AppFontIcon ? CardIconType.AppFontIcon : CardIconType.FontAwesomeIcon,
        Icon: icon,
        IconColor: iconColor
    };
}

function buildBadge(column, row, locale) {
    const value = row[column.FieldName];

    if (column.FieldType === TableFieldType.Checkbox) {
        if (value !== true) return null;
    } else if (value === undefined || value === null || value === "") {
        return null;
    }

    let label;
    let option = null;
    if (column.FieldType === TableFieldType.Checkbox) {
        label = column.BadgeText || column.Label;
    } else if (column.FieldType === TableFieldType.Select && column.Options) {
        option = column.Options.find(opt => opt.Key === value);
        label = option ? option.Value : value;
    } else {
        label = formatCardValue(column, row, locale);
    }

    // Per-row label read from another row field, for badges whose text varies by row (e.g. a status
    // plus the date it was recorded). Mirrors BadgeIconField; the style still comes from the column
    // or the matched option.
    if (column.BadgeLabelField) {
        const labelValue = row[column.BadgeLabelField];
        if (labelValue) label = labelValue;
    }

    let style = column.BadgeStyle;
    if (option && option.BadgeStyle !== undefined) {
        style = option.BadgeStyle;
    }

    let icon = column.BadgeIcon;
    if (option && option.Icon !== undefined) {
        icon = option.Icon;
    } else if (column.BadgeIconField) {
        const fieldValue = row[column.BadgeIconField];
        if (fieldValue) icon = fieldValue;
    }

    return {
        Label: label,
        Icon: icon,
        IconType: column.BadgeIconType,
        Style: style ?? BadgeStyle.Default,
        Title: column.BadgeTitle,
        Alignment: column.BadgeAlignment || BadgeAlignment.Left,
        Large: column.BadgeLarge === true
    };
}

function mapColumnsToCardItem(props) {
    const { Columns, Row, IdField, Locale, RowButtons } = props;

    const titleColumns = Columns.filter(column => column.CardPosition === CardPosition.Title);
    const subtitleColumns = Columns.filter(column => column.CardPosition === CardPosition.Subtitle);
    const bodyColumns = Columns.filter(column => column.CardPosition === CardPosition.Body);
    const badgeColumns = Columns.filter(column => column.CardPosition === CardPosition.Badge);

    const titleText = titleColumns
        .map(column => formatCardValue(column, Row, Locale))
        .filter(text => text !== "")
        .join(" · ");

    const subtitleText = subtitleColumns
        .map(column => formatCardValue(column, Row, Locale))
        .filter(text => text !== "")
        .join(" · ");

    const visibleBodyColumns = bodyColumns
        .map(column => ({ column, text: formatCardValue(column, Row, Locale) }))
        .filter(entry => entry.text !== "");

    const body = visibleBodyColumns.length > 0
        ? (
            <>
                {visibleBodyColumns.map((entry) => (
                    <div key={entry.column.FieldName} className="eone-ui-card-body-text">
                        {entry.column.CardBodyLabel
                            ? <span className="eone-ui-card-body-label">{entry.column.Label}: </span>
                            : null}
                        {entry.text}
                    </div>
                ))}
            </>
        )
        : null;

    const badges = badgeColumns
        .map(column => buildBadge(column, Row, Locale))
        .filter(badge => badge !== null);

    const iconParts = buildIcon(props);

    let titleVersion = null;
    const versionField = props.CardVersionField;
    if (versionField && Row[versionField] !== undefined && Row[versionField] !== null) {
        const count = props.CardVersionCountField ? Row[props.CardVersionCountField] : null;
        if (count === null || count > 1) {
            titleVersion = `v${Row[versionField]}`;
        }
    }

    const actions = (RowButtons || [])
        .filter(button => !(button.Hidden && Row[button.Hidden]))
        .map(button => ({
            Icon: button.Icon,
            Title: button.Title,
            Label: button.Title,
            OnClick: button.OnClick,
            Cancel: button.Cancel
        }));

    return {
        Key: Row[IdField],
        Title: titleText,
        TitleVersion: titleVersion,
        Subtitle: subtitleText,
        Body: body,
        Badges: badges,
        Actions: actions,
        ...iconParts
    };
}

function TableCard(props) {
    const item = mapColumnsToCardItem(props);
    return (
        <Card
            Layout={CardLayout.Grid}
            Item={item}
            Selected={item.Key !== undefined && item.Key !== null && item.Key === props.SelectedRowId}
            OnSelect={props.OnSelect}
            OnDoubleClick={props.OnDoubleClick}
            Reorderable={props.Reorderable}
            RowStyle={props.Row && props.Row.style}
            DataTestId={props.DataTestId} />
    );
}

export { TableCard };
